// Plain, JSON-safe mirrors of the contract's state messages. The store
// holds these — never proto Message instances — so Solid's fine-grained
// reactivity sees plain objects and IndexedDB can structured-clone the
// state as it is. Timestamps become ISO strings, the stream sequence a
// decimal string (uint64 does not fit a JS number).
import type {
  AccountStatus,
  Child,
  Delta,
  Down,
  HistoryEvent,
  Line,
  Lines,
  Note,
  PendingAction,
  PendingSend,
  ServiceStatus,
  Snapshot,
  TaskState,
} from "@/gen/psypen/v1/session_pb";
import { timestampDate } from "@bufbuild/protobuf/wkt";
import type { Timestamp } from "@bufbuild/protobuf/wkt";

function iso(ts: Timestamp | undefined): string | null {
  if (!ts) return null;
  return timestampDate(ts).toISOString();
}

export type StoredChild = {
  kind: string;
  url: string;
  title: string;
};

export type StoredLine = {
  id: string;
  name: string;
  kind: string;
  worker: string;
  need: string;
  nextFiring: string | null;
  unread: number;
  open: string[];
  done: string[];
  detail: string;
  children: StoredChild[];
  last: string;
  description: string;
};

export type StoredPendingSend = {
  tool: string;
  target: string;
  lang: string;
  text: string;
  subject: string;
  editable: boolean;
};

export type StoredPendingAction = {
  id: string;
  kind: string;
  sends: StoredPendingSend[];
  line: string;
  expiresAt: string | null;
};

// Local is true on an optimistic entry the UI minted before the server's
// own event for the same turn arrived (id 0 marks a not-stored event; a
// stored one replaces its local twin by turn).
export type StoredHistoryEvent = {
  id: number;
  turn: string;
  at: string | null;
  kind: number;
  text: string;
  tool: string;
  input: string;
  error: boolean;
  local?: boolean;
};

export type StoredNote = {
  id: string;
  source: string;
  title: string;
  text: string;
  raw: string;
  started: string | null;
  updated: string | null;
};

export type StoredAccountStatus = {
  id: string;
  name: string;
  connected: boolean;
  reason: string;
};

export type StoredServiceStatus = {
  slug: string;
  label: string;
  connected: boolean;
  canInstall: boolean;
  accounts: StoredAccountStatus[];
};

export type StoredTask = { state: string; text: string };

// The whole of what one session shows, one version of it in IndexedDB.
// Seq is the event stream's sequence the state is current to, "" before
// the first snapshot.
export type SessionState = {
  seq: string;
  pending: StoredPendingAction | null;
  lines: StoredLine[];
  history: StoredHistoryEvent[];
  notes: StoredNote[];
  connections: StoredServiceStatus[];
  task: StoredTask | null;
};

export function emptyState(): SessionState {
  return {
    seq: "",
    pending: null,
    lines: [],
    history: [],
    notes: [],
    connections: [],
    task: null,
  };
}

export function childFromProto(c: Child): StoredChild {
  return { kind: c.kind, url: c.url, title: c.title };
}

export function lineFromProto(l: Line): StoredLine {
  return {
    id: l.id,
    name: l.name,
    kind: l.kind,
    worker: l.worker,
    need: l.need,
    nextFiring: iso(l.nextFiring),
    unread: l.unread,
    open: [...l.open],
    done: [...l.done],
    detail: l.detail,
    children: l.children.map(childFromProto),
    last: l.last,
    description: l.description,
  };
}

function pendingSendFromProto(s: PendingSend): StoredPendingSend {
  return {
    tool: s.tool,
    target: s.target,
    lang: s.lang,
    text: s.text,
    subject: s.subject,
    editable: s.editable,
  };
}

export function pendingFromProto(p: PendingAction | undefined): StoredPendingAction | null {
  if (!p) return null;
  return {
    id: p.id,
    kind: p.kind,
    sends: p.sends.map(pendingSendFromProto),
    line: p.line,
    expiresAt: iso(p.expiresAt),
  };
}

export function historyFromProto(h: HistoryEvent): StoredHistoryEvent {
  return {
    id: Number(h.id),
    turn: h.turn,
    at: iso(h.at),
    kind: h.kind,
    text: h.text,
    tool: h.tool,
    input: h.input,
    error: h.error,
  };
}

function noteFromProto(n: Note): StoredNote {
  return {
    id: n.id,
    source: n.source,
    title: n.title,
    text: n.text,
    raw: n.raw,
    started: iso(n.started),
    updated: iso(n.updated),
  };
}

function accountFromProto(a: AccountStatus): StoredAccountStatus {
  return { id: a.id, name: a.name, connected: a.connected, reason: a.reason };
}

function serviceFromProto(s: ServiceStatus): StoredServiceStatus {
  return {
    slug: s.slug,
    label: s.label,
    connected: s.connected,
    canInstall: s.canInstall,
    accounts: s.accounts.map(accountFromProto),
  };
}

export function snapshotToState(snap: Snapshot): SessionState {
  return {
    seq: snap.seq.toString(),
    pending: pendingFromProto(snap.pending),
    lines: snap.lines.map(lineFromProto),
    history: snap.history.map(historyFromProto),
    notes: snap.notes.map(noteFromProto),
    connections: snap.connections.map(serviceFromProto),
    task: null,
  };
}

// One stored state event, extracted from a Down: the state frames the
// store keeps, or null when the frame is a turn's own traffic (transcript,
// reply, audio — PRODUCT-321+ listens to those straight off the client).
export type StateEvent =
  | { kind: "snapshot"; state: SessionState }
  | { kind: "delta"; delta: Delta }
  | { kind: "task"; task: StoredTask };

export function stateEventFromDown(d: Down): StateEvent | null {
  switch (d.msg.case) {
    case "snapshot":
      return { kind: "snapshot", state: snapshotToState(d.msg.value) };
    case "delta":
      return { kind: "delta", delta: d.msg.value };
    case "task":
      // TaskState rides the standing socket (no turn id) as a task's pill.
      if (d.turn === "") return { kind: "task", task: taskFromProto(d.msg.value) };
      return null;
    default:
      return null;
  }
}

function taskFromProto(t: TaskState): StoredTask {
  return { state: t.state, text: t.text };
}

export function linesFromProto(l: Lines): StoredLine[] {
  return l.lines.map(lineFromProto);
}
